import { useParams } from "wouter";
import Layout, { PageHeader } from "@/components/Layout";
import NextStepFooter from "@/components/NextStepFooter";
import { Card } from "@/components/ui/Cards";
import { Construction, Sparkles } from "lucide-react";
import type { SpineStep } from "@/lib/spine";

interface StubProps {
  title: string;
  subtitle: string;
  description: string;
  bullets: string[];
  spineKey?: SpineStep["key"];
}

export default function Stub({ title, subtitle, description, bullets, spineKey }: StubProps) {
  const params = useParams<{ id?: string }>();
  return (
    <Layout projectId={params.id}>
      <PageHeader title={title} subtitle={subtitle} />
      <div className="p-8 max-w-3xl space-y-4 pb-40">
        <Card>
          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
            >
              <Construction size={18} style={{ color: "#F97316" }} />
            </div>
            <div>
              <p className="text-sm font-medium text-white">We haven't built this yet.</p>
              <p className="mt-1 text-sm" style={{ color: "#9CA3AF" }}>
                {description}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start gap-3 mb-3">
            <Sparkles size={14} style={{ color: "#F97316" }} className="mt-0.5" />
            <p className="text-sm font-medium text-white">When it's done, you'll be able to:</p>
          </div>
          <ul className="space-y-2 ml-7">
            {bullets.map((b, i) => (
              <li key={i} className="text-sm leading-relaxed" style={{ color: "#D1D5DB" }}>
                — {b}
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {spineKey && <NextStepFooter currentStep={spineKey} projectId={params.id!} />}
    </Layout>
  );
}

export const AboutUsStub = () => (
  <Stub
    title="About Us"
    subtitle="Your agency's positioning, voice, and past work"
    description="This is where you capture what makes your agency unique — your positioning, brand voice, past wins and losses, team capabilities, and any existing relationship with this client."
    bullets={[
      "Fill in your agency's positioning and unique value proposition",
      "Add case studies, past wins, and relevant past work that demonstrates capability",
      "Describe your team's expertise and how you'd approach this prospect's challenge",
      "Note any existing relationship or context that matters for this pitch",
      "Every AI step (strategy, drafts, research) reads from this to write more effective proposals",
    ]}
    spineKey="about-us"
  />
);

export const AboutClientStub = () => (
  <Stub
    title="About Client"
    subtitle="What you know about the prospect"
    description="Research, insights, and context about the prospect — their market, business model, decision-makers, and any intel you've gathered about them."
    bullets={[
      "Add links to the client's website, social profiles, recent news, or annual reports",
      "Note who the decision-makers are and what you know about them",
      "Capture their market position, recent moves, and strategic priorities",
      "Add any internal notes from past conversations or pitches to similar clients",
      "The AI reads this context to write proposals that speak their language and address their real priorities",
    ]}
    spineKey="about-client"
  />
);

export const ContextBuilderStub = () => (
  <Stub
    title="Background"
    subtitle="What you know about the requester and your own agency."
    description="This is where you'll capture the context the AI uses to write a sharper proposal — the requester's goals and constraints on one side, your agency's positioning, case studies, and team on the other."
    bullets={[
      "Fill in the requester's business context manually, or have the AI extract it from the RFP",
      "Fill in your agency's context manually, or have the AI extract it from uploaded documents (case studies, brand guides, past proposals)",
      "Add links to relevant websites or social pages for the AI to reference",
      "Save once — every later AI step (research, strategy, drafts) reads from this context",
    ]}
  />
);

export const ResearchBriefStub = () => (
  <Stub
    title="Research"
    subtitle="What we know about the requester's world."
    description="A short briefing the AI prepares from the RFP plus your background notes — covering the organization, their priorities, the market they operate in, and what their competitors are doing."
    bullets={[
      "Generate a starter research brief in ~20 seconds",
      "Edit each section by hand to layer in things the AI couldn't know",
      "Add sources (URLs, uploaded reports) the AI should pull from",
      "Re-generate sections individually if you want a fresh take",
    ]}
  />
);

export const StrategyBriefStub = () => (
  <Stub
    title="Strategy"
    subtitle="The angle for this proposal — themes, positioning, risks."
    description="The strategic plan for THIS specific proposal: which themes you'll lean on, how you'll position against likely competitors, the creative ideas that anchor the response, and the risks to watch."
    bullets={[
      "Generate three to five 'win themes' the proposal will reinforce in every section",
      "Get a sharp positioning statement and recommended angle against likely competitors",
      "Surface creative ideas, pricing considerations, and risks specific to this client",
      "Edit any of it freely — the AI gives you a starting opinion, not a final answer",
    ]}
    spineKey="strategy"
  />
);

export const ProposalDraftsStub = () => (
  <Stub
    title="Drafts"
    subtitle="AI-written first drafts of every section."
    description="For each requirement on the Requirements page, you'll get a written draft section here. The AI reads your about-us, about-client, and strategy, then writes prose you can edit instead of staring at a blank page."
    bullets={[
      "Click any requirement → 'Write draft' → watch the text appear character-by-character",
      "Edit drafts directly in the page; changes save automatically",
      "Use 'Improve' actions: tighten, expand, add more evidence, plain-English rewrite",
      "Add comments to flag a section for a teammate to review",
    ]}
    spineKey="drafts"
  />
);

export const RequiredDocumentsStub = () => (
  <Stub
    title="Attachments"
    subtitle="The supporting files the RFP asks you to include."
    description="A checklist of the documents the RFP requires (insurance certificates, references, sample work, etc.). Upload each one as you collect it; the export will bundle them with the proposal."
    bullets={[
      "Auto-populated from the RFP's required-documents list",
      "Upload PDFs / images / Word files — they store securely in the cloud",
      "Mark each doc complete or not-applicable as the team collects them",
      "Export the proposal as a ZIP with everything in one place",
    ]}
    spineKey="attachments"
  />
);

export const QAReviewStub = () => (
  <Stub
    title="Final Check"
    subtitle="A senior-reviewer pass over the whole proposal before you submit."
    description="Once the drafts are done, the AI does a critical reading of the entire proposal — looking for missing answers, weak evidence, compliance gaps, and anything that could lower the score."
    bullets={[
      "Run a one-click review using a more thorough AI model",
      "Findings grouped by severity: critical, warning, suggestion",
      "Each finding links you to the exact section to fix",
      "Re-run after fixes to confirm everything's clean before you submit",
    ]}
    spineKey="qa"
  />
);

export const RFPSummaryStub = () => (
  <Stub
    title="Summary"
    subtitle="The key details from the RFP, pulled out automatically."
    description="The core facts the AI extracts from the RFP — deadline, deliverables, evaluation criteria, budget, required documents."
    bullets={[
      "Click 'Read RFP' — the AI extracts a structured summary in 10–30 seconds",
      "Edit any field by hand if the AI got it slightly wrong",
      "Re-read after the RFP changes (addenda are common)",
      "Every later step reads from this summary",
    ]}
  />
);
